import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Sparkles,
  ShieldCheck,
  Stethoscope,
  Zap,
  Smile,
  Wrench,
  Crown,
  Scan,
  Activity,
  Braces,
  ArrowRight,
  CheckCircle2,
  Heart,
  Award,
} from "lucide-react";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Reviews } from "@/components/Reviews";
import { ClinicGallery } from "@/components/ClinicGallery";
import { CLINIC, getOpenStatus } from "@/data/constants";
import { Button } from "@/components/ui/button";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.6, delay: i * 0.08 } }),
};

const services = [
  { icon: Stethoscope, title: "General & Preventive", desc: "Routine check-ups, cleanings and preventive care to keep smiles healthy." },
  { icon: Activity, title: "Root Canal Treatment", desc: "Painless endodontic therapy to save and restore damaged teeth." },
  { icon: Scan, title: "Dental Implants", desc: "Permanent, natural-looking tooth replacement solutions." },
  { icon: Crown, title: "Zirconia Crowns", desc: "Strong, aesthetic tooth-coloured crowns and bridges." },
  { icon: Sparkles, title: "Teeth Whitening", desc: "Brighten your smile with safe professional whitening." },
  { icon: Braces, title: "Clear Aligners & Braces", desc: "Discreet orthodontic solutions for beautifully aligned teeth." },
  { icon: Zap, title: "Laser Dentistry", desc: "Advanced laser treatments for precise, comfortable care." },
  { icon: Wrench, title: "Tooth Extractions & Fillings", desc: "Gentle extractions and tooth-coloured restorations." },
];

const expertise = [
  "General & Preventive Dentistry",
  "Cosmetic Smile Enhancement",
  "Root Canal Treatment (Endodontics)",
  "Dental Implants",
  "Laser Dentistry",
  "Tooth-Colored Fillings",
  "Crowns & Bridges",
  "Zirconia Crowns",
  "Teeth Whitening",
  "Clear Aligners",
  "Tooth Extractions",
  "Preventive Oral Health Care",
];

const whyChoose = [
  "Gentle and compassionate approach",
  "Personalized treatment planning",
  "Modern dental equipment and techniques",
  "Focus on painless dentistry",
  "Strong emphasis on patient education",
  "Comfortable, hygienic, welcoming clinic",
];

export default function Home() {
  const status = getOpenStatus();
  return (
    <div className="bg-sage-50 min-h-screen">
      <Header />

      {/* Hero */}
      <section className="relative overflow-hidden brand-gradient grain-texture">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-16 lg:py-24 grid lg:grid-cols-2 gap-12 items-center">
          <motion.div initial="hidden" animate="show" variants={fadeUp}>
            <span className="inline-flex items-center gap-2 bg-white/10 border border-white/20 backdrop-blur text-sage-50 text-xs font-700 uppercase tracking-wider px-3 py-1.5 rounded-full">
              <Heart size={13} /> Women-Owned Clinic · Nagpur
            </span>
            <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl font-700 text-white tracking-tight leading-[1.05] mt-5">
              Healthy, confident{" "}
              <span className="text-gold-light italic">smiles</span> for life.
            </h1>
            <p className="text-sage-100 text-lg mt-5 max-w-lg leading-relaxed">
              At Kalpataru Dental Care, Dr. Ruchika Sontakay blends modern dental technology
              with a gentle, personalised approach for a stress-free experience.
            </p>
            <div className="flex flex-wrap gap-3 mt-8">
              <Link to="/contact" data-testid="hero-book-btn">
                <Button className="rounded-full bg-gold hover:bg-gold-dark text-sage-900 font-700 h-12 px-7 text-base">
                  Book an Appointment <ArrowRight size={18} className="ml-1" />
                </Button>
              </Link>
              <a href="#services">
                <Button
                  variant="outline"
                  className="rounded-full border-white/30 bg-transparent text-white hover:bg-white/10 h-12 px-7 text-base font-700"
                  data-testid="hero-services-btn"
                >
                  Explore Services
                </Button>
              </a>
            </div>
            <div className="flex items-center gap-8 mt-10">
              <div>
                <div className="font-heading text-3xl text-white leading-none">5000+</div>
                <div className="text-xs text-sage-200 font-600 mt-1">Happy Patients</div>
              </div>
              <div className="w-px h-10 bg-white/20" />
              <div>
                <div className="font-heading text-3xl text-white leading-none">2019</div>
                <div className="text-xs text-sage-200 font-600 mt-1">Trusted Since</div>
              </div>
              <div className="w-px h-10 bg-white/20" />
              <span className="inline-flex items-center gap-2 text-sm font-600 text-sage-100">
                <span className={`w-2.5 h-2.5 rounded-full ${status.open ? "bg-emerald-400 animate-pulse" : "bg-sage-300"}`} />
                {status.label}
              </span>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.7 }}
            className="relative"
          >
            <div className="absolute -inset-4 bg-white/5 rounded-[2.5rem] -rotate-2" />
            <img
              src={CLINIC.heroImage}
              alt="Kalpataru Dental Care clinic"
              className="relative rounded-[2rem] w-full h-[440px] object-cover shadow-xl"
            />
            <div className="absolute bottom-5 left-5 bg-white/95 backdrop-blur rounded-2xl px-5 py-4 shadow-lg">
              <div className="flex items-center gap-2">
                <ShieldCheck className="text-sage-500" size={22} />
                <div>
                  <div className="font-heading text-xl text-sage-900 leading-none">Painless</div>
                  <div className="text-xs text-sage-500 font-600">Modern Dentistry</div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Doctor */}
      <section id="doctor" className="max-w-7xl mx-auto px-5 sm:px-8 py-20 scroll-mt-24">
        <div className="grid lg:grid-cols-5 gap-12 items-start">
          <motion.div
            className="lg:col-span-2"
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
            variants={fadeUp}
          >
            <div className="relative">
              <div className="absolute -inset-3 bg-sage-800 rounded-3xl rotate-2" />
              <img
                src={CLINIC.doctorPhoto}
                alt="Dr. Ruchika Vivek Sontakay"
                data-testid="doctor-photo"
                className="relative rounded-3xl w-full object-cover shadow-lg"
              />
            </div>
          </motion.div>

          <motion.div
            className="lg:col-span-3"
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
            variants={fadeUp}
          >
            <span className="text-gold-dark font-700 text-sm uppercase tracking-wider">
              Meet Your Dentist
            </span>
            <h2 className="font-heading text-4xl sm:text-5xl text-sage-900 tracking-tight mt-2">
              Dr. Ruchika Vivek Sontakay <span className="text-sage-400 text-3xl">(BDS)</span>
            </h2>
            <p className="text-sage-500 font-600 mt-1">Founder & Chief Dental Surgeon</p>
            <span className="inline-flex items-center gap-2 bg-gold/15 text-gold-dark font-700 text-sm px-3 py-1.5 rounded-full mt-3" data-testid="doctor-experience-badge">
              <Award size={15} /> 7+ Years of Experience in Dentistry
            </span>
            <p className="font-heading text-2xl text-sage-700 italic mt-4 leading-snug">
              "Creating healthy, confident smiles through compassionate and modern dental care."
            </p>
            <div className="space-y-4 text-sage-600 mt-5 leading-relaxed">
              <p>
                Dr. Ruchika Vivek Sontakay is a dedicated dental surgeon committed to providing
                comprehensive, patient-centered dental care for individuals and families. As the
                founder of Kalpataru Dental Care, she combines clinical expertise with a gentle,
                personalized approach to ensure every patient enjoys a comfortable and
                stress-free dental experience.
              </p>
              <p>
                She believes that every smile is unique and deserves the highest standard of
                care. By utilizing modern dental technology and evidence-based treatment methods,
                Dr. Sontakay focuses on preserving oral health while enhancing the aesthetics of
                every smile.
              </p>
            </div>

            <div className="grid sm:grid-cols-2 gap-8 mt-8">
              <div>
                <h3 className="font-heading text-2xl text-sage-900 mb-3">Areas of Expertise</h3>
                <ul className="space-y-2">
                  {expertise.map((e) => (
                    <li key={e} className="flex items-start gap-2 text-sm text-sage-600">
                      <CheckCircle2 size={16} className="text-sage-400 mt-0.5 shrink-0" /> {e}
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <h3 className="font-heading text-2xl text-sage-900 mb-3">Why Patients Choose Dr. Ruchika</h3>
                <ul className="space-y-2">
                  {whyChoose.map((e) => (
                    <li key={e} className="flex items-start gap-2 text-sm text-sage-600">
                      <Smile size={16} className="text-gold-dark mt-0.5 shrink-0" /> {e}
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="mt-8 bg-sage-100 border-l-4 border-sage-400 rounded-r-xl p-6">
              <h3 className="font-heading text-xl text-sage-900 mb-1">Philosophy of Care</h3>
              <p className="text-sage-600 italic leading-relaxed">
                "A healthy smile is an investment in your confidence and overall well-being. My
                goal is to help every patient achieve lifelong oral health through honest advice,
                quality treatment, and compassionate care."
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Services */}
      <section id="services" className="bg-sage-100/60 py-20 scroll-mt-24">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div className="max-w-2xl">
            <span className="text-gold-dark font-700 text-sm uppercase tracking-wider">
              What We Offer
            </span>
            <h2 className="font-heading text-4xl sm:text-5xl text-sage-900 tracking-tight mt-2">
              Comprehensive Dental Services
            </h2>
            <p className="text-sage-600 mt-3">
              From routine check-ups to advanced restorative and cosmetic procedures — all under
              one roof with strict sterilization protocols.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 mt-12">
            {services.map((s, i) => (
              <motion.div
                key={s.title}
                custom={i}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true }}
                variants={fadeUp}
                data-testid={`service-card-${i}`}
                className="bg-white rounded-2xl border border-sage-200 p-6 hover:-translate-y-1 transition-transform duration-300 hover:shadow-md"
              >
                <div className="w-12 h-12 rounded-xl bg-sage-100 flex items-center justify-center mb-4">
                  <s.icon className="text-sage-500" size={24} />
                </div>
                <h3 className="font-heading text-2xl text-sage-900 leading-tight">{s.title}</h3>
                <p className="text-sm text-sage-600 mt-2 leading-relaxed">{s.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* About */}
      <section id="about" className="max-w-7xl mx-auto px-5 sm:px-8 py-20 scroll-mt-24">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp}>
            <img
              src={CLINIC.aboutImage}
              alt="Kalpataru Dental Care interior"
              className="rounded-3xl w-full h-[420px] object-cover shadow-lg"
            />
          </motion.div>
          <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp}>
            <span className="text-gold-dark font-700 text-sm uppercase tracking-wider">
              About Us
            </span>
            <h2 className="font-heading text-4xl sm:text-5xl text-sage-900 tracking-tight mt-2">
              About Kalpataru Dental Care
            </h2>
            <div className="space-y-4 text-sage-600 mt-5 leading-relaxed">
              <p>
                Established in 2019, Kalpataru Dental Care is a state-of-the-art dental clinic
                committed to providing comprehensive oral healthcare in a modern, comfortable,
                and patient-centric environment. Equipped with advanced dental technology and
                contemporary treatment facilities, the clinic offers a full spectrum of
                preventive, restorative, cosmetic, and specialized dental services while
                maintaining the highest standards of sterilization and patient safety.
              </p>
              <p>
                Our mission is to make high-quality dental care accessible to every patient by
                combining clinical excellence, personalized treatment plans, and compassionate
                care. We strive to create a welcoming experience where every patient receives
                individualized attention and the latest advancements in dentistry for healthier,
                more confident smiles.
              </p>
            </div>
            <Link to="/contact" data-testid="about-book-btn">
              <Button className="rounded-full bg-sage-800 hover:bg-sage-900 text-white font-700 h-12 px-7 mt-7">
                Schedule Your Visit <ArrowRight size={18} className="ml-1" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Our Clinic */}
      <ClinicGallery />

      {/* Reviews */}
      <Reviews />

      <Footer />
    </div>
  );
}
